import { useCallback } from 'react';
import _ from 'lodash';
import moment from 'moment';

import apiActions from '../../../actions/apiActions';
import calendarActions from '../../../actions/calendarActions';

/**
 * Запись изменений, сделанных мышью на диаграмме. Правка оптимистичная: сначала в сцену,
 * потом запрос, при ошибке значения возвращаются как были.
 *
 * Дату считаем не по бару, а по записи: берём её собственное значение и сдвигаем на целое
 * число шагов масштаба. Бар — величина приблизительная, его границы выровнены по сетке
 * ленты, и записывать их значило бы терять то, чего масштаб не показывает: минуты в разрезе
 * дня, день месяца в разрезе года. Сдвиг же переносит остаток как есть — 14:45 в разрезе
 * часа превращается в 15:45.
 *
 * Сдвиг приходит из адаптера уже целым числом шагов масштаба: минутных масштабов у нас
 * нет, значит и менять дату по минутам незачем. Какие именно края человек трогал,
 * адаптер тоже разбирает сам и передаёт сюда флагами.
 */
const useTaskEdit = ({ records, catalogId, sceneId, startId, endId, progressId, stepUnit, cell }) =>
  useCallback(
    ({ id, steps, movedStart, movedEnd, progress }) => {
      const canResize = Boolean(endId && endId !== startId);
      if (steps && movedStart !== movedEnd && !canResize) return false;

      const values = {};
      const record = records && records.find((r) => r.get('id') === id);
      const rawStart = startId && record && record.getIn(['values', startId]);
      const rawEnd = endId && record && record.getIn(['values', endId]);

      const shift = (date) => moment(date).add(steps, stepUnit).toDate().toISOString();

      if (startId && movedStart && steps && rawStart) values[startId] = shift(rawStart);

      if (canResize && movedEnd && steps) {
        if (rawEnd) {
          values[endId] = shift(rawEnd);
        } else if (!movedStart && rawStart) {
          // У записи без даты конца бар синтетический — он растянут на одну ячейку масштаба.
          // При перетаскивании целиком SVAR отдаёт оба края, и записать такой конец значит
          // придумать дату, которой не было. Пишем её, только если человек тянул именно
          // за правый край: тогда в патче нет начала
          values[endId] = moment(rawStart).add(cell.step, cell.unit).add(steps, stepUnit).toDate().toISOString();
        }
      }

      if (steps && movedStart !== movedEnd && rawStart && (values[endId] || rawEnd)) {
        const nextStart = moment(values[startId] || rawStart);
        const nextEnd = moment(values[endId] || rawEnd);
        if (nextEnd.isValid() && !nextEnd.isAfter(nextStart)) return false;
      }

      if (progressId && progress != null) values[progressId] = progress;
      if (_.isEmpty(values)) return;

      calendarActions.updateRecordBeforeMakeRequest({ recordId: id }, values, sceneId);
      apiActions
        .updateRecord({ catalogId, recordId: id }, { values }, { sceneId })
        .catch(() => calendarActions.updateRecordBeforeMakeRequest({ recordId: id }, undefined, sceneId));
    },
    [records, catalogId, sceneId, startId, endId, progressId, stepUnit, cell],
  );

export default useTaskEdit;
